import { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import {
  Handle,
  NodeProps,
  Position,
  useEdges,
  useNodes,
  Node,
  Edge,
} from 'reactflow';
import {
  Typography,
  Box,
  IconButton,
  Paper,
  Tooltip,
  lighten,
  useTheme,
} from '@mui/material';
import { EditorContext } from '../context';
import { ReactComponent as AssetIcon } from '../../../../assets/icons/main/plc-icon.svg';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/main/delete-icon.svg';
import { ReactComponent as AddCircleIcon } from '../../../../assets/icons/main/plus-circle-icon.svg';
import { ReactComponent as AlertIcon } from '../../../../assets/icons/main/alertIcon.svg';
import { MachineSchema, MachineUISchema } from '../panel-schemas';
import { MachineNode } from './Machine';
import { ParamsPanel } from '../panels/ParamsPanel';
import { has } from 'lodash';
import { palette } from '../../../../theme/palette';
import { useOutletContext } from 'react-router-dom';
import { get } from 'lodash';
import { useDispatch } from 'react-redux';
import { factoryManagementActions } from '../../../../store/reducers/factory-management';
import ElipsisTypography from '../components/ElipsisTypography';

import NodeStatuses from './NodeStatuses';

const Styles = {
  nodeWrapper: {
    height: '48px',
    position: 'relative',
    // border: '1px solid #D9DFED',
    borderRadius: '12px',
    justifyContent: 'space-between',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '400px',
  },
  iconWrapper: {
    width: '48px',
    height: '48px',
    borderRadius: '11px 0 0 11px',
    backgroundColor: palette.info.main,
  },
  icon: {
    height: '48px',
    width: '48px',
  },
  handleSource: {
    height: '1px',
    width: '1px',
    backgroundColor: 'transparent',
    left: 0,
  },
  handleTarget: {
    height: '1px',
    width: '1px',
    backgroundColor: 'transparent',
    right: 0,
  },
  addIcon: {
    position: 'absolute',
    left: '-36px',
  },
  assetNodeWrapper: {
    width: '355px',
    borderRadius: '18px',
    borderBottom: '6px solid #82A6E0',
    // backgroundColor: '#FFF',
    boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.2), -2px 0px 4px rgba(0, 0, 0, 0.2)',
  },
  header: {
    height: '79px',
    display: 'flex',
    alignItems: 'center',
    padding: '24px 24px 14px 24px',
    borderBottom: '1px dashed #82A6E0',
    boxSizing: 'border-box',
  },
  // titleWrapper: {
  //   display: 'flex',
  //   alignItems: 'baseline',
  // },
  title: { fontSize: '1.1rem', marginLeft: 1 },
  address: { fontSize: '0.8rem', marginLeft: 1, fontWeight: 400 },
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    alignItems: 'center',
    boxSizing: 'border-box',
    padding: '24px',
    gap: '16px',
  },
  scanContainer: {
    height: 'auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
    backgroundColor: '#F8FAFB',
    padding: '24px 24px 8px 24px',
    boxSizing: 'border-box',
  },
  scanText: { fontSize: '1rem', fontWeight: 400, color: '#434D67' },
  scanWrapper: { display: 'flex', justifyContent: 'space-between', gap: '16px' },
  scanField: { width: '100%', height: '42px', flexShrink: 1 },
  btnsWrapper: { position: 'relative', display: 'flex', flexDirection: 'row', alignSelf: 'stretch' },
  btn: { width: 'auto', flexGrow: 1, height: '42px', color: '#64759F', borderColor: '#64759F' },
  scanBtn: {
    width: '109px',
    height: '42px',
    fontSize: '1rem',
    backgroundColor: '#82A6E0',
    flexShrink: 0,
    flexGrow: 1,
  },
  labelConnectedIcons: {
    width: 'calc(100% - 50px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  nodeLabel: {
    fontSize: '1.1rem',
    marginLeft: 1,
    width: '200px',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
};

export const AssetNode: FunctionComponent<NodeProps> = ({ id, type, data = {}, xPos, yPos, ...props }: NodeProps) => {
  const { onBtnClick, removeNode, editable, hasAccessTo, isLiveView } = useContext(EditorContext);
  const nodes = useNodes();
  const thisNode: any = useMemo(() => nodes.find((node: Node) => node.id === id) as Node, [id, nodes]);
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false);
  const themeData: any = useTheme();
  const themeMode: any = get(useOutletContext(), 'themeMode', {});
  const dispatch = useDispatch();
  const edges = useEdges();
  const thisEdge: Edge = useMemo(() => edges.find((edge: Edge) => edge.target === id) as Edge, [id, edges]);

  useEffect(() => {
    const _assets = nodes.filter((item: any) => {
      return item.type === 'assetNode';
    });
    if (_assets.length) {
      const _filteredAssets = _assets.filter((items: any) => {
        if (items.connectedTo === thisNode.connectedTo) {
          if (
            (items.data && items.data.name === thisNode.data.name) ||
            (items.data && items.data.ip === thisNode.data.ip)
          ) {
            if (has(items.data, 'name') && has(items.data, 'ip')) {
              if (items.data.name.trim() !== '' && items.data.ip.trim() !== '') {
                return items;
              }
            }
          }
        }
      });

      if (_filteredAssets.length > 1) {
        setIsDuplicate(true);
      } else {
        setIsDuplicate(false);
      }
    }
  }, [nodes, thisNode]);

  const TooltipAssetComponent = (
    <Tooltip
      title={
        <Typography variant='subtitle1' style={{ fontSize: '0.8rem' }}>
          {isDuplicate ? 'Duplicate asset name or ip address found' : 'Please enter asset name and IP address.'}
        </Typography>
      }
      placement='left'
      arrow
    >
      <AlertIcon style={{ height: '24px', width: '24px', marginRight: '4px', marginLeft: '4px', color: palette.warning.main }} />
    </Tooltip>
  );

  const hasReadOnlyAccess = !(hasAccessTo.create && hasAccessTo.update);

  return (
    <>
      <Handle style={Styles.handleTarget} type='target' position={Position.Right} />
      <Handle style={Styles.handleSource} type='source' position={Position.Left} />
      <Paper
        elevation={0}
        sx={{
          ...Styles.nodeWrapper,
          border: `1px solid ${themeData.palette.customBorderColor.main}`,
          // background: isDuplicate ? palette.warning.dark : (data.enabled) ? (data.ip && data.name) ? (data.name.trim() === '' || data.ip.trim() === '') ? palette.warning.dark : themeData.palette.background.paper : palette.warning.dark : themeData.palette.background.paper }}>
          background: isDuplicate
            ? palette.warning.dark
            : data.enabled
            ? data.ip && data.name
              ? data.name.trim() === '' || data.ip.trim() === ''
                ? palette.warning.dark
                : themeMode.isLightBG
                ? themeData.palette.background.paper
                : lighten(themeData.palette.background.default, 0.08)
              : palette.warning.dark
            : themeMode.isLightBG
            ? themeData.palette.background.paper
            : lighten(themeData.palette.background.default, 0.2),
        }}
      >
        {editable && (
          <Tooltip title={'Link a machine'}>
            <IconButton
              sx={Styles.addIcon}
              onClick={() =>
                onBtnClick('assetNode', {
                  id: 'machine',
                  type: 'machineNode',
                  display: 'Machine',
                  node: MachineNode,
                  sourcePosition: Position.Left,
                  targetPosition: Position.Right,
                  connectedTo: id,
                  panel: {
                    custom: true,
                    component: ParamsPanel,
                    schema: MachineSchema,
                    uiSchema: MachineUISchema,
                  },
                })
              }
              disabled={hasReadOnlyAccess}
            >
              <AddCircleIcon
                style={{ color: themeData.palette.custom.light, opacity: hasReadOnlyAccess ? '0.5' : 1 }}
              />
            </IconButton>
          </Tooltip>
        )}
        <Box sx={Styles.iconWrapper}>
          <IconButton
            style={Styles.icon}
            onClick={() => {
              onBtnClick('assetNode', { id, data });
              // For newly imported/added assets, backend has no persisted component set yet.
              // Fetching immediately can return empty options and wipe local Excel components.
              if (thisNode?.operation !== 'added') {
                dispatch(factoryManagementActions.getAssetComponentsDataRequest(id));
              }
            }}
          >
            <AssetIcon style={{ color: palette.primary.contrastText }} />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center', // alert icon and text vertically centered
            maxWidth: '300px',
            minHeight: '2.6em',
            width: '100%',
          }}
        >
          {/* Alert icon logic (always same size, always centered) */}
          {isDuplicate ? (
            <Box sx={{ minWidth: 32, minHeight: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {TooltipAssetComponent}
            </Box>
          ) : data.enabled ? (
            data.ip && data.name ? (
              data.name.trim() === '' || data.ip.trim() === '' ? (
                <Box
                  sx={{ minWidth: 32, minHeight: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  {TooltipAssetComponent}
                </Box>
              ) : null
            ) : (
              <Box
                sx={{ minWidth: 32, minHeight: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                {TooltipAssetComponent}
              </Box>
            )
          ) : null}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              gap: '2px',
              fontSize: '1rem',
            }}
          >
            {/* Name: separate line, ellipsis/tooltip if long */}
            <ElipsisTypography width={250}>{data.name && data.name.trim() ? data.name : 'Asset'}</ElipsisTypography>
            {/* IP: separate line, ellipsis/tooltip if long, in parentheses */}
            {data.ip && (
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', fontSize: '0.88rem' }}>
                <ElipsisTypography width={250}>({data.ip})</ElipsisTypography>
              </Box>
            )}
          </Box>
        </Box>
        <Box display={'flex'} alignItems={'center'} justifyContent={'end'}>
          {editable ? (
            <IconButton disabled={!editable || hasReadOnlyAccess} onClick={() => removeNode(id)}>
              <DeleteIcon style={{ color: themeData.palette.custom.light, opacity: hasReadOnlyAccess ? '0.5' : 1 }} />
            </IconButton>
          ) : (
            <Box sx={{ width: 38 }} />
          )}
        </Box>
        {isLiveView && (
          <NodeStatuses
            statuses={{
              mqttStatus: thisEdge?.data?.mqttStatus,
              plcStatus: thisEdge?.data?.plcStatus,
              dbStatus: thisEdge?.data?.dbStatus,
            }}
            palette={themeData.palette}
          />
        )}
      </Paper>
    </>
  );
};
